import { useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { Download, InsertDriveFile, UploadFile } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { normalizarData } from "@/components/import/dataPlanilha";
import { normalizarHora } from "@/components/import/horaPlanilha";

// Componente genérico de importação por planilha: baixa um modelo .xlsx, e
// ao confirmar lê e valida (client-side) a planilha que o usuário anexou,
// entregando as linhas já parseadas pra quem o usa decidir o que fazer com
// elas (resolver nome->id de referências da entidade, chamar o Method de
// import em lote). Não sabe nada sobre colaborador/cargo/setor/etc — a
// única entidade que hoje o usa é EscalaColaboradoresTab, mas a ideia é
// reaproveitar em toda tela de configuração que precisar de importação em
// massa, só trocando `colunas` e `onImportar`.
//
// Fluxo é deliberadamente só três passos: anexa -> confirma -> resultado
// (sucesso ou lista de problemas). Sem prévia da planilha e sem aviso
// prévio — o usuário já sabe o que está enviando.

export interface ColunaImportacao {
  chave: string;
  rotulo: string;
  obrigatoria?: boolean;
  exemplo?: string;
  // Coluna acrescentada depois: planilha feita com o modelo antigo, sem ela, ainda importa (vazia).
  podeFaltarNoCabecalho?: boolean;
  // "data": a célula é lida como DD/MM/AAAA (ou serial do Excel) e enviada como AAAA-MM-DD.
  // "hora": lida como HH:MM (ou a hora que o Excel guarda como fração do dia) e enviada como HH:MM.
  tipo?: "data" | "hora";
  // Rótulos antigos do cabeçalho que continuam valendo, pra planilhas já baixadas.
  rotulosAlternativos?: string[];
}

export interface LinhaImportacaoErro {
  // 1-based, contando só as linhas de dados enviadas (sem o cabeçalho) —
  // mesma convenção usada pelos Methods escala.importar_* no backend.
  linha: number;
  erro: string;
  // Na linha repetida, a primeira com o mesmo valor, na mesma contagem; vira "(já está na linha N)".
  linha_anterior?: number;
}

export interface ResultadoImportacao {
  importados: number;
  erros: LinhaImportacaoErro[];
  // Texto de sucesso no lugar do padrão, quando a tela tem mais a dizer (ex.: escalas que não mudaram).
  mensagem?: { texto: string; tipo: "success" | "warning" };
}

export type LinhaPlanilha = Record<string, string>;

// Aba só de consulta no modelo baixado (ex.: as cargas horárias que a coluna aceita). Vai depois
// da aba "Modelo"; a importação lê só a primeira aba.
export interface AbaConsulta {
  nome: string;
  linhas: string[][];
}

interface ImportadorPlanilhaProps {
  titulo: string;
  nomeArquivoModelo: string;
  colunas: ColunaImportacao[];
  onImportar: (linhas: LinhaPlanilha[]) => Promise<ResultadoImportacao>;
  onConcluido?: () => void;
  abasDeConsulta?: AbaConsulta[];
  // Texto do botão que abre o importador; muda quando a tela tem mais de um.
  rotuloBotao?: string;
}

export function ImportadorPlanilha({
  titulo,
  nomeArquivoModelo,
  colunas,
  onImportar,
  onConcluido,
  abasDeConsulta = [],
  rotuloBotao = "Importar planilha",
}: ImportadorPlanilhaProps) {
  const [open, setOpen] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erros, setErros] = useState<LinhaImportacaoErro[]>([]);
  // Linha do arquivo de cada linha enviada: converte a linha do erro (índice no que foi enviado) na
  // que o usuário vê no Excel, contando o cabeçalho e as linhas em branco puladas.
  const [linhasNoArquivo, setLinhasNoArquivo] = useState<number[]>([]);
  const [importados, setImportados] = useState<number | null>(null);
  const [mensagem, setMensagem] = useState<ResultadoImportacao["mensagem"]>();
  const [erroArquivo, setErroArquivo] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setArquivo(null);
    setErros([]);
    setImportados(null);
    setMensagem(undefined);
    setErroArquivo(null);
  };

  const handleOpen = () => {
    reset();
    setOpen(true);
  };

  const handleClose = () => {
    if (enviando) return;
    setOpen(false);
  };

  const baixarModelo = () => {
    const cabecalho = colunas.map((c) => c.rotulo);
    const exemplo = colunas.map((c) => c.exemplo ?? "");
    const planilha = XLSX.utils.aoa_to_sheet([cabecalho, exemplo]);
    const livro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(livro, planilha, "Modelo");
    for (const aba of abasDeConsulta) {
      XLSX.utils.book_append_sheet(livro, XLSX.utils.aoa_to_sheet(aba.linhas), aba.nome);
    }
    XLSX.writeFile(livro, nomeArquivoModelo);
  };

  const handleSelecionarArquivo = (file: File) => {
    setArquivo(file);
    setErros([]);
    setImportados(null);
    setMensagem(undefined);
    setErroArquivo(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  // Lê e valida (client-side) o .xlsx anexado — colunas esperadas no
  // cabeçalho, ao menos uma linha de dados — devolvendo já as linhas no
  // formato que onImportar espera. Só roda quando o usuário confirma, não
  // ao anexar: não existe mais uma etapa de prévia separada da de envio.
  const parseArquivo = async (
    file: File,
  ): Promise<{ linhas: LinhaPlanilha[]; linhasNoArquivo: number[]; errosFormato: LinhaImportacaoErro[] }> => {
    const buffer = await file.arrayBuffer();
    const livro = XLSX.read(buffer);
    const nomeAba = livro.SheetNames[0];
    const aba = nomeAba ? livro.Sheets[nomeAba] : undefined;
    if (!aba) {
      throw new Error("A planilha não tem nenhuma aba.");
    }
    // Com as linhas em branco, para saber a linha de cada uma no arquivo; elas saem logo abaixo.
    const inicio = XLSX.utils.decode_range(aba["!ref"] ?? "A1").s.r;
    const linhasPreenchidas = XLSX.utils.sheet_to_json<unknown[]>(aba, { header: 1, blankrows: true })
      .map((valores, i) => ({ valores, linhaNoArquivo: inicio + i + 1 }))
      .filter(({ valores }) => valores.some((v) => String(v ?? "").trim() !== ""));

    if (linhasPreenchidas.length < 2) {
      throw new Error("A planilha não tem nenhuma linha de dados abaixo do cabeçalho.");
    }

    const [primeira, ...dados] = linhasPreenchidas;
    const cabecalho = primeira?.valores ?? [];
    const posicaoPorColuna = colunas.map((coluna) => ({
      coluna,
      idx: cabecalho.findIndex((h) => {
        const texto = String(h ?? "").trim().toLowerCase();
        return [coluna.rotulo, ...(coluna.rotulosAlternativos ?? [])].some((r) => r.toLowerCase() === texto);
      }),
    }));
    const faltando = posicaoPorColuna.filter(({ coluna, idx }) => idx === -1 && !coluna.podeFaltarNoCabecalho);
    if (faltando.length > 0) {
      throw new Error(`Coluna(s) ausente(s) no cabeçalho: ${faltando.map((f) => f.coluna.rotulo).join(", ")}`);
    }

    const errosFormato: LinhaImportacaoErro[] = [];
    const parsed: LinhaPlanilha[] = dados.map(({ valores: linha }, i) => {
      const obj: LinhaPlanilha = {};
      for (const { coluna, idx } of posicaoPorColuna) {
        if (idx === -1) {
          obj[coluna.chave] = "";
        } else if (coluna.tipo === "data") {
          const data = normalizarData(linha[idx]);
          if (data === null) {
            errosFormato.push({ linha: i + 1, erro: `${coluna.rotulo}: data inválida, use DD/MM/AAAA` });
          }
          obj[coluna.chave] = data ?? "";
        } else if (coluna.tipo === "hora") {
          const hora = normalizarHora(linha[idx]);
          if (hora === null) {
            errosFormato.push({ linha: i + 1, erro: `${coluna.rotulo}: hora inválida, use HH:MM` });
          }
          obj[coluna.chave] = hora ?? "";
        } else {
          obj[coluna.chave] = String(linha[idx] ?? "").trim();
        }
      }
      return obj;
    });

    return { linhas: parsed, linhasNoArquivo: dados.map((d) => d.linhaNoArquivo), errosFormato };
  };

  const camposFaltandoEm = (linha: LinhaPlanilha) => colunas.filter((c) => c.obrigatoria && !linha[c.chave]);

  const confirmar = async () => {
    if (!arquivo) return;
    setEnviando(true);
    setErroArquivo(null);
    setErros([]);
    setImportados(null);
    setMensagem(undefined);
    try {
      const { linhas, linhasNoArquivo, errosFormato } = await parseArquivo(arquivo);
      setLinhasNoArquivo(linhasNoArquivo);
      if (errosFormato.length > 0) {
        setErros(errosFormato);
        return;
      }

      const linhasComCampoFaltando = linhas
        .map((linha, i) => ({ linha: i + 1, faltando: camposFaltandoEm(linha) }))
        .filter((l) => l.faltando.length > 0);
      if (linhasComCampoFaltando.length > 0) {
        setErros(
          linhasComCampoFaltando.map(({ linha, faltando }) => ({
            linha,
            erro: `campo(s) obrigatório(s) não preenchido(s): ${faltando.map((c) => c.rotulo).join(", ")}`,
          })),
        );
        return;
      }

      const resultado = await onImportar(linhas);
      setImportados(resultado.importados);
      setMensagem(resultado.mensagem);
      setErros(resultado.erros ?? []);
      if (resultado.importados > 0 && (resultado.erros ?? []).length === 0) {
        onConcluido?.();
      }
    } catch (e: any) {
      setErroArquivo(e?.message || "Erro ao importar planilha");
    } finally {
      setEnviando(false);
    }
  };

  const noArquivo = (linha: number) => linhasNoArquivo[linha - 1] ?? linha + 1;
  const textoDoErro = (e: LinhaImportacaoErro) =>
    `Linha ${noArquivo(e.linha)}: ${e.erro}` + (e.linha_anterior ? ` (já está na linha ${noArquivo(e.linha_anterior)})` : "");

  const jaConcluido = importados !== null && erros.length === 0;
  const podeConfirmar = arquivo !== null && !enviando && !jaConcluido;

  return (
    <>
      <Button variant="outlined" startIcon={<UploadFile />} onClick={handleOpen}>
        {rotuloBotao}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{titulo}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" startIcon={<Download />} onClick={baixarModelo}>
                Baixar modelo
              </Button>
              <Button variant="outlined" component="label" startIcon={<UploadFile />}>
                Selecionar planilha
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleSelecionarArquivo(file);
                  }}
                />
              </Button>
            </Stack>

            {arquivo && (
              <Stack direction="row" spacing={1} alignItems="center">
                <InsertDriveFile fontSize="small" color="action" />
                <Typography variant="body2">{arquivo.name}</Typography>
              </Stack>
            )}

            {erroArquivo && <Alert severity="error">{erroArquivo}</Alert>}

            {erros.length > 0 && (
              <Alert severity="error">
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                  Nada foi importado — corrija os problemas abaixo na planilha e envie de novo:
                </Typography>
                <List dense disablePadding>
                  {erros.map((e, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemText primary={textoDoErro(e)} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            {jaConcluido && (
              <Alert severity={mensagem?.tipo ?? "success"}>
                {mensagem?.texto ?? `${importados} registro(s) importado(s) com sucesso.`}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={enviando}>
            {jaConcluido ? "Fechar" : "Cancelar"}
          </Button>
          <Button variant="contained" onClick={confirmar} disabled={!podeConfirmar}>
            Confirmar importação
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
